import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

function valueDisplay(entry) {
  if (entry == null) return '—';
  const t = entry.setting_type;
  if (t === 'json') {
    try {
      return JSON.stringify(entry.setting_value, null, 2);
    } catch (_e) {
      return String(entry.setting_value);
    }
  }
  if (t === 'boolean') return entry.setting_value ? 'true' : 'false';
  return entry.setting_value != null ? String(entry.setting_value) : '—';
}

export default function PlatformSystemSettingDetailPage() {
  const { settingId } = useParams();
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/system-settings/${settingId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setEntry(json.entry);
    } catch (e) {
      setError(e.message);
      setEntry(null);
    } finally {
      setLoading(false);
    }
  }, [settingId]);

  useEffect(() => {
    load();
  }, [load]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      const res = await platformApiFetch(`/api/platform/system-settings/${settingId}`, { method: 'DELETE' });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Delete failed');
      }
      navigate('/platform/system-settings');
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (user?.role !== 'super_admin') {
    return <Navigate to="/platform/analytics" replace />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !entry) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!entry) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
        {value ?? '—'}
      </Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">{entry.setting_label}</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {entry.setting_group}.{entry.setting_key}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <Chip size="small" label={entry.setting_type} />
            <Chip size="small" label={entry.status} variant="outlined" />
            {entry.is_sensitive && <Chip size="small" label="Sensitive" color="warning" />}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" component={Link} to={`/platform/system-settings/${settingId}/edit`}>
            Edit
          </Button>
          <Button color="error" variant="outlined" onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
          <Button variant="outlined" component={Link} to="/platform/system-settings">
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {row('Description', entry.description)}
          {row('Updated', entry.updated_at ? String(entry.updated_at) : '—')}
        </Grid>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Value
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 1,
            p: 1,
            bgcolor: 'grey.100',
            borderRadius: 1,
            fontSize: '0.85rem',
            overflow: 'auto',
            maxHeight: 320
          }}
        >
          {valueDisplay(entry)}
        </Box>
      </Paper>

      <Dialog open={confirmDelete} onClose={() => !deleting && setConfirmDelete(false)}>
        <DialogTitle>Delete this setting?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the key permanently. Applications that read this setting may break until it is recreated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={doDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
